import { useEffect, useState } from "react";
import type { CloneReportConfigurationReportPayload, PlanConfig, ReportConfig } from "../types";
import { SelectPicker } from "./SelectPicker";

interface CloneReportDialogProps {
    open: boolean;
    plans: PlanConfig[];
    sourcePlan?: PlanConfig;
    sourceReport?: ReportConfig;
    loading?: boolean;
    loadingFoldersClientId?: number | null;
    folderLoadErrorClientId?: number | null;
    folderLoadError?: string;
    error?: string;
    onLoadFolders: (clientId: number) => void | Promise<void>;
    onCancel: () => void;
    onClone: (payload: CloneReportConfigurationReportPayload) => void | Promise<void>;
}

export function CloneReportDialog({
    open,
    plans,
    sourcePlan,
    sourceReport,
    loading = false,
    loadingFoldersClientId = null,
    folderLoadErrorClientId = null,
    folderLoadError = "",
    error = "",
    onLoadFolders,
    onCancel,
    onClone,
}: CloneReportDialogProps) {
    const [newClientId, setNewClientId] = useState(0);
    const [newFolderId, setNewFolderId] = useState(0);
    const [newReportName, setNewReportName] = useState("");
    const [folderLoadAttemptClientId, setFolderLoadAttemptClientId] = useState<number | null>(null);
    const [openPicker, setOpenPicker] = useState<"" | "healthPlan" | "folder">("");

    useEffect(() => {
        if (!open || !sourceReport) return;

        setNewClientId(sourcePlan?.clientId || 0);
        setNewFolderId(0);
        setNewReportName(`${sourceReport.label || "Report"} - Copy`.slice(0, 160));
        setFolderLoadAttemptClientId(null);
        setOpenPicker("");
    }, [open, sourcePlan?.clientId, sourceReport?.id]);

    const selectedPlan = plans.find((plan) => Number(plan.clientId || 0) === newClientId);
    const selectedFolders = selectedPlan?.reportFolders || [];
    const foldersLoading = !!newClientId && loadingFoldersClientId === newClientId;
    const selectedFolderLoadError = folderLoadErrorClientId === newClientId ? folderLoadError : "";

    useEffect(() => {
        if (!open || !newClientId || !selectedPlan || foldersLoading || folderLoadAttemptClientId === newClientId) return;
        if (!selectedPlan.reportPlanId && selectedPlan.reportFolders.length === 0) {
            setFolderLoadAttemptClientId(newClientId);
            void onLoadFolders(newClientId);
        }
    }, [folderLoadAttemptClientId, foldersLoading, newClientId, onLoadFolders, open, selectedPlan]);

    if (!open || !sourceReport) return null;

    const trimmedName = newReportName.trim();
    const hasFolders = selectedFolders.length > 0;
    const nameTooLong = trimmedName.length > 160;
    const canClone = !!sourceReport.reportId
        && !!newClientId
        && !!newFolderId
        && !!trimmedName
        && !nameTooLong
        && hasFolders
        && !foldersLoading
        && !loading;

    return (
        <div className="rc-confirm-layer rc-clone-layer" role="presentation" onMouseDown={() => !loading && onCancel()}>
            <section
                aria-labelledby="rc-clone-report-title"
                aria-modal="true"
                className="rc-confirm-panel rc-clone-panel"
                role="dialog"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <div className="rc-confirm-visual" aria-hidden="true">
                    <span className="rc-confirm-orbit">
                        <span className="material-icons md-18">content_copy</span>
                    </span>
                </div>
                <div className="rc-confirm-copy">
                    <span className="rc-confirm-kicker">Report Configuration</span>
                    <h3 id="rc-clone-report-title">Clone Report</h3>
                    <p>Create a new report from the selected report configuration.</p>
                </div>
                <button className="rc-confirm-close" type="button" aria-label="Cancel clone" disabled={loading} onClick={onCancel}>
                    <span className="material-icons md-18" aria-hidden="true">close</span>
                </button>

                <div className="rc-clone-form">
                    <label>
                        <span>Source Report</span>
                        <input value={sourceReport.label} readOnly />
                    </label>
                    <label>
                        <span>Health Plan <strong aria-hidden="true">*</strong></span>
                        <SelectPicker
                            options={plans
                                .filter((plan) => !!plan.clientId)
                                .map((plan) => ({
                                    id: plan.clientId || plan.id,
                                    value: String(plan.clientId || ""),
                                    name: plan.clientName || plan.name,
                                }))}
                            value={newClientId ? String(newClientId) : ""}
                            placeholder="Select Health Plan..."
                            searchPlaceholder="Search Health Plans..."
                            disabled={loading}
                            open={openPicker === "healthPlan"}
                            onOpenChange={(isOpen) => setOpenPicker(isOpen ? "healthPlan" : "")}
                            onChange={(value) => {
                                setNewClientId(Number(value || 0));
                                setNewFolderId(0);
                                setFolderLoadAttemptClientId(null);
                                setOpenPicker("");
                            }}
                        />
                    </label>
                    <label>
                        <span>Report Folder <strong aria-hidden="true">*</strong></span>
                        <SelectPicker
                            options={selectedFolders
                                .filter((folder) => !!folder.reportFolderId)
                                .map((folder) => ({
                                    id: folder.reportFolderId || folder.id,
                                    value: String(folder.reportFolderId || ""),
                                    name: folder.label,
                                }))}
                            value={newFolderId ? String(newFolderId) : ""}
                            placeholder={foldersLoading ? "Loading folders..." : hasFolders ? "Select Report Folder..." : "No folders available"}
                            searchPlaceholder="Search Report Folders..."
                            disabled={loading || foldersLoading || !hasFolders}
                            open={openPicker === "folder"}
                            onOpenChange={(isOpen) => setOpenPicker(isOpen ? "folder" : "")}
                            onChange={(value) => {
                                setNewFolderId(Number(value || 0));
                                setOpenPicker("");
                            }}
                        />
                        {!foldersLoading && !hasFolders && (
                            <small>{selectedFolderLoadError || "This Health Plan has no report folders. Choose another Health Plan before cloning."}</small>
                        )}
                    </label>
                    <label>
                        <span>New Report Name <strong aria-hidden="true">*</strong></span>
                        <input
                            maxLength={160}
                            value={newReportName}
                            disabled={loading}
                            onChange={(event) => setNewReportName(event.target.value)}
                        />
                        <small>{nameTooLong ? "Maximum 160 characters." : `${trimmedName.length}/160`}</small>
                    </label>
                    {error && <p className="rc-clone-error">{error}</p>}
                </div>

                <div className="rc-confirm-actions">
                    <button className="rc-confirm-button rc-confirm-cancel" type="button" disabled={loading} onClick={onCancel}>
                        Cancel
                    </button>
                    <button className="rc-confirm-button rc-confirm-primary" type="button" disabled={!canClone} onClick={() => void onClone({ newReportName: trimmedName, newClientId, newFolderId })}>
                        <span className="material-icons md-16" aria-hidden="true">{loading ? "sync" : "content_copy"}</span>
                        <span>{loading ? "Cloning..." : "Clone Report"}</span>
                    </button>
                </div>
            </section>
        </div>
    );
}
